import React, { useCallback } from "react";
import { ICONS, TRANSACTION_CATEGORIES } from "../constants";
import { amountDataToDisplay } from "../utils/utils";
import { useTranslation } from 'react-i18next';
import VenmoBadge from "./VenmoBadge";
import ZelleBadge from "./ZelleBadge";
import "./RowHighlight.css";

export default function TransactionRow(props) {
  const { t } = useTranslation();
  const {
    modalId,
    setTargetTransaction,
    transaction,
    setNewTransaction,
    isSettled = false,
    onRequestDelete,
  } = props;
  const contributor = transaction?.contributor;

  const openDetail = useCallback(() => {
    setNewTransaction(false);
    setTargetTransaction(transaction);
    // Let React flush state first, then show the modal (avoids stale transaction details).
    window.setTimeout(() => {
      const el = document.getElementById(modalId);
      if (!el || !window?.bootstrap?.Modal) return;
      try {
        const inst =
          window.bootstrap.Modal.getInstance(el) || new window.bootstrap.Modal(el);
        inst.show();
      } catch (e) {
        // ignore
      }
    }, 0);
  }, [modalId, setNewTransaction, setTargetTransaction, transaction]);

  const dataReady = () => {
    // Be tolerant: backend can legitimately send null category/icon.
    // Only require the minimal fields needed to render a row.
    return Boolean(transaction && contributor && contributor.name);
  };

  if (!dataReady()) return null;

  const categoryEmoji =
    (transaction?.category && TRANSACTION_CATEGORIES?.[transaction.category]) ||
    "🧾";
  const contributorEmoji =
    (contributor?.icon && ICONS?.[contributor.icon]) || "🐾";
  const contributorName = contributor?.name || (t("general.unknown") || "Unknown");

  return (
    <div
      className="as-row-surface d-flex align-items-center justify-content-between"
    >
      <div
        className="as-row-click-zone truncate d-flex align-items-center"
        style={{ minWidth: 0 }}
        role="button"
        tabIndex={0}
        onClick={openDetail}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openDetail();
          }
        }}
        aria-label={
          isSettled
            ? (t("transaction.title") || "Transaction")
            : (t("transaction.edit_transaction") || "Edit transaction")
        }
        title={
          isSettled
            ? (t("transaction.title") || "Transaction")
            : (t("transaction.edit_transaction") || "Edit transaction")
        }
      >
        <div className="as-row-leading-badge me-2" aria-hidden="true">
          <span className="fs-4">{categoryEmoji}</span>
        </div>
        <div style={{ minWidth: 0 }}>
          <div className="truncate as-row-title">{transaction.title}</div>
          <div className="as-row-subtitle">
            <span className="d-inline-flex align-items-center gap-2">
              <span>
                {contributorEmoji} {contributorName}
              </span>
              <span className="d-inline-flex align-items-center gap-1">
                <VenmoBadge username={contributor.venmoUsername} />
                <ZelleBadge
                  zelleUsername={contributor.zelleUsername}
                  zelleRecipientName={contributor.zelleRecipientName}
                />
              </span>
            </span>
          </div>
        </div>
      </div>
      <div className="d-flex align-items-center gap-2">
        <span className="as-row-pill" aria-label={t("transaction.amount") || "Amount"}>
          ${amountDataToDisplay(transaction.amount)}
        </span>
        <i className="bi bi-chevron-right as-row-chevron" aria-hidden="true" />
      <button
        className={isSettled ? "btn btn-sm" : "btn btn-outline-danger btn-sm"}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          if (isSettled) return;
          if (typeof onRequestDelete === "function") onRequestDelete(transaction);
        }}
        disabled={isSettled}
        aria-label={
          isSettled
            ? (t("group.settled") || "Settled")
            : (t("general.delete") || "Delete")
        }
        title={
          isSettled
            ? (t("group.settled") || "Settled")
            : (t("general.delete") || "Delete")
        }
        style={isSettled ? { color: '#dc3545', cursor: 'not-allowed' } : {}}
      >
        <i className={isSettled ? "bi-lock-fill" : "bi-trash-fill"} />
      </button>
      </div>
    </div>
  );
}
